import { FC } from 'react'

import { Box, Spinner, Text } from '@factorialco/deprecated-design-system'
import {
  EmployeeCurrentAndNextContractVersionsDocument,
  EmployeesPersonalDataDocument,
  ResourceFieldDataFragment,
  TaskDetailAffecteeReferenceContractFragment,
  TaskDetailContractContextlessPoliciesFragment,
  TaskDetailCustomFieldsContextlessPoliciesFragment,
  TaskDetailJobCatalogContextlessPoliciesFragment,
  ValueDataFragment,
} from 'generated/resources/graphql'
import { observable } from 'mobx'
import moment from 'moment'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import createAsyncData from 'modules/core/hooks/createAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { i18nDateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { FixFlowProvider } from 'modules/contracts/components/FixFlow/FixFlowProvider'
import useFetchLegalEntityContractData from 'modules/contracts/hooks/useFetchLegalEntityContractData'
import { ContractCuesProvider } from 'modules/contracts/lib/contractCuesContext'
import { ContractFlowProvider } from 'modules/contracts/lib/contractFlowContext'
import { LegalEntityContractDataProvider } from 'modules/contracts/lib/legalEntityContractDataContext'
import contractVersionsPayrollPolicies, {
  ContractVersionsPayrollPoliciesProvider,
} from 'modules/contracts/store/collections/contractVersionsPayrollPolicies'
import jobSalaryRangesCollection from 'modules/job_catalog/store/collections/salaryRanges'
import policies, { PoliciesProvider } from 'modules/payroll_bundle/store/collections/policies'
import { DataCollectionCategories } from 'modules/processes/domain'
import UploadDocuments from 'modules/tasks/components/TaskDetails/UploadDocuments'
import { TaskDetail } from 'modules/tasks/components/TaskElement/useTaskDetail'

import ContractData from './ContractData'
import EmployeeData from './EmployeeData'
import Footer from './Footer'
import { IDAndSocialSecurityUploads } from './IDAndSocialSecurityUploads'
import Warning from './Warning'

interface Props {
  task: TaskDetail
  canSeePersonalInfo: boolean
  canEditPersonalInfo: boolean
  canEmployeeManageAnyEmployeeFolder: boolean
  customResourceFieldsData: ResourceFieldDataFragment[]
  customValuesData: ValueDataFragment[]
  hasDevTimeTrackingWorkableDaysDefinition?: boolean
  hasTimeTrackingAnnualisationDailyValue?: boolean
  referenceContract?: TaskDetailAffecteeReferenceContractFragment | null
  jobCatalogContextlessPolicies: TaskDetailJobCatalogContextlessPoliciesFragment | undefined
  hasPayrollBundle: boolean
  contractContextlessPolicies: TaskDetailContractContextlessPoliciesFragment | undefined
  customFieldsContextlessPolicies: TaskDetailCustomFieldsContextlessPoliciesFragment | undefined
}

export interface DataCollectionCategory {
  category: DataCollectionCategories
  configuration: {
    folder_id?: string
    onboarding_folder_id?: string
    field_ids?: string[]
  }
}

export const getVisibleCategories = (
  categories: DataCollectionCategory[],
  canSeePersonalInfo: boolean
): DataCollectionCategory[] =>
  canSeePersonalInfo
    ? categories
    : categories.filter(
        (targetCategory) =>
          targetCategory.category === DataCollectionCategories.employee_questions_cf
      )

const CONTRACT_CATEGORY = 'contract'

const allowedUploadFolderCategories = (): Partial<
  Record<
    DataCollectionCategories,
    {
      title: string
      description: string
    }
  >
> => ({
  [DataCollectionCategories.bankInformation]: {
    title: i18n.t(
      'processes.process_settings.tasks.form.super_tasks_module.data_collection.bank_certificate.title'
    ),
    description: i18n.t(
      'processes.process_settings.tasks.form.super_tasks_module.data_collection.bank_certificate.description'
    ),
  },
  [DataCollectionCategories.identificationNumber]: {
    title: i18n.t(
      'processes.process_settings.tasks.form.super_tasks_module.data_collection.id_certificate.title'
    ),
    description: i18n.t(
      'processes.process_settings.tasks.form.super_tasks_module.data_collection.id_certificate.description'
    ),
  },
})

const useFetchData = createAsyncData((contractVersionId: string) => {
  return Promise.all([
    contractVersionsPayrollPolicies.fetch({
      data: {
        contract_version_ids: [contractVersionId],
      },
    }),
    policies.fetch({ data: { include_archived: true } }),
    jobSalaryRangesCollection.fetch(),
  ])
})

const DataCollection = observer(
  ({
    task,
    canSeePersonalInfo,
    canEditPersonalInfo,
    canEmployeeManageAnyEmployeeFolder,
    customResourceFieldsData,
    customValuesData,
    hasDevTimeTrackingWorkableDaysDefinition,
    hasTimeTrackingAnnualisationDailyValue,
    referenceContract,
    jobCatalogContextlessPolicies,
    hasPayrollBundle,
    contractContextlessPolicies,
    customFieldsContextlessPolicies,
  }: Props) => {
    const configurationFields = JSON.parse(task.dataCollectionTypeData?.configurationFields || '')
    const employeeId = task.dataCollectionTypeData?.employeeId
    const afecteeEmployee = task.affecteesConnection.edges[0]?.node?.employee

    const { employee: currentEmployee, company } = useCurrentSession()

    const { data: employeeDocument } = useQuery(EmployeesPersonalDataDocument, {
      variables: {
        companyId: company.id,
        employeeId: afecteeEmployee?.id ?? '0',
      },
      skip: !afecteeEmployee?.id,
    })

    const employeeData = employeeDocument?.employees.employeesConnection.edges[0]?.node

    const { data: employeeContractsData } = useQuery(
      EmployeeCurrentAndNextContractVersionsDocument,
      {
        variables: {
          ids: [afecteeEmployee?.id ?? '0'],
          activeOn: i18nDateToString(moment(), 'backend'),
        },
        skip: !afecteeEmployee?.id,
      }
    )

    if (!afecteeEmployee) return null
    if (!afecteeEmployee.legalEntity) return null

    const contractVersionIdFromQuery =
      employeeContractsData?.employees.employeesConnection.nodes[0]?.contractVersions[0]?.id
    const contractVersionId = contractVersionIdFromQuery || referenceContract?.id

    const loadingContractData = useFetchData(contractVersionId)
    const legalEntityContractData = useFetchLegalEntityContractData(
      afecteeEmployee.legalEntity.id,
      afecteeEmployee.legalEntity.country
    )

    const {
      loading: contractDataLoading,
      spanishContractData,
      frenchContractData,
      germanContractData,
      portugueseContractData,
      commonContractData,
    } = legalEntityContractData

    if (contractDataLoading) return <Loading />
    if (!contractVersionId) return <Loading />

    const readonly = !canEditPersonalInfo
    const categories: DataCollectionCategory[] = configurationFields[0].categories
    const visibleCategories = getVisibleCategories(categories, canSeePersonalInfo)
    const category = visibleCategories[0]?.category
    const hasFolder = categories[0]?.configuration?.folder_id
    const folderConfiguration = categories[0]?.configuration

    const isContractCategories = configurationFields[0].resource === CONTRACT_CATEGORY
    const contractFieldIds = isContractCategories
      ? categories.flatMap((cat) => cat.configuration?.field_ids).filter(Boolean)
      : undefined

    const uploadDocumentText = category && allowedUploadFolderCategories()[category]
    const isIdentificationNumber = category === DataCollectionCategories.identificationNumber

    const showAttachments = employeeId && uploadDocumentText && hasFolder
    const showSocialAndId = category && isIdentificationNumber && employeeId

    const title = (cat: DataCollectionCategories) => {
      switch (cat) {
        case DataCollectionCategories.address:
          return i18n.t(
            'processes.process_settings.wizard.tasks.types.request_general_information.preview.address.title'
          )
        case DataCollectionCategories.fiscal_residence:
          return i18n.t(
            'processes.process_settings.wizard.tasks.types.request_general_information.preview.fiscal_residence.title'
          )
        case DataCollectionCategories.emergency_contact:
          return i18n.t('employee_form.emergency_contact.title')
        case DataCollectionCategories.generalInformation:
          return i18n.t(
            'processes.process_settings.wizard.tasks.types.request_general_information.preview.general.title'
          )
        case DataCollectionCategories.personalSituation:
          return i18n.t(
            'processes.process_settings.wizard.tasks.types.request_general_information.preview.personal_situation.title'
          )
        case DataCollectionCategories.start_date:
          return i18n.t(
            'processes.process_settings.wizard.tasks.types.contract_data.preview.start_date.title'
          )
        case DataCollectionCategories.role:
          return i18n.t(
            'processes.process_settings.wizard.tasks.types.contract_data.preview.role.title'
          )
        case DataCollectionCategories.salary:
          return i18n.t(
            'processes.process_settings.wizard.tasks.types.contract_data.preview.salary.title'
          )
        case DataCollectionCategories.contract_hours:
          return i18n.t(
            'processes.process_settings.wizard.tasks.types.contract_data.preview.contract_hours.title'
          )
        case DataCollectionCategories.contract_custom_fields:
          return i18n.t(
            'processes.process_settings.wizard.tasks.types.contract_data.preview.contract_custom_fields.title'
          )
        default:
          return ''
      }
    }

    if (!employeeData || !employeeData.legalEntity) return null

    const categoriesElements = () => {
      return visibleCategories.map((targetCategory) => {
        const fields = targetCategory.configuration?.field_ids ?? []
        const filteredFields = customResourceFieldsData.filter((field) => {
          const fieldId = field.id
          if (!fieldId) return false
          return fields.includes(fieldId)
        })
        return (
          <Box gap="s8" key={targetCategory.category} paddingBottom="s8">
            <Text.Normal size="300" color="grey800" weight="medium">
              {title(targetCategory.category)}
            </Text.Normal>
            <Box background="grey200" borderRadius={{ all: 'abs004' }} padding="s32">
              {isContractCategories ? (
                <ContractData
                  legalEntity={afecteeEmployee.legalEntity}
                  category={targetCategory.category}
                  hasDevTimeTrackingWorkableDaysDefinition={
                    hasDevTimeTrackingWorkableDaysDefinition
                  }
                  hasTimeTrackingAnnualisationDailyValue={hasTimeTrackingAnnualisationDailyValue}
                  referenceContract={referenceContract}
                  jobCatalogContextlessPolicies={jobCatalogContextlessPolicies}
                  affecteeEmployee={task.affecteesConnection.edges[0]?.node?.employee}
                  hasPayrollBundle={hasPayrollBundle}
                  contractContextlessPolicies={contractContextlessPolicies}
                  customFieldsContextlessPolicies={customFieldsContextlessPolicies}
                />
              ) : (
                <EmployeeData
                  employeeData={employeeData}
                  legalEntity={afecteeEmployee.legalEntity}
                  readonly={readonly}
                  isCurrent={currentEmployee.id === employeeData?.id}
                  category={targetCategory.category}
                  canSeePersonalInfo={canSeePersonalInfo}
                  canEditPersonalInfo={canEditPersonalInfo}
                  customResourceFieldsData={filteredFields}
                  customValuesData={customValuesData}
                  hasPayrollBundle={hasPayrollBundle}
                />
              )}
            </Box>
          </Box>
        )
      })
    }

    const contractVersionPayrollPolicy = contractVersionsPayrollPolicies.isEmpty
      ? null
      : (contractVersionsPayrollPolicies.models[0] ?? null)

    if (loadingContractData) {
      return (
        <Box width="full" height="full" justifyContent="center" alignItems="center">
          <Spinner />
        </Box>
      )
    }

    if (visibleCategories.length === 0) {
      return <Warning />
    }

    return (
      <ContractFlowProvider
        salaryRanges={jobSalaryRangesCollection.models}
        contractVersionPayrollPolicy={contractVersionPayrollPolicy}
        legalEntityContractData={legalEntityContractData}
      >
        <LegalEntityContractDataProvider
          spanishContractData={spanishContractData}
          frenchContractData={frenchContractData}
          germanContractData={germanContractData}
          portugueseContractData={portugueseContractData}
          commonContractData={commonContractData}
        >
          <ContractVersionsPayrollPoliciesProvider collection={contractVersionsPayrollPolicies}>
            <PoliciesProvider collection={policies}>
              <FixFlowProvider
                fieldIds={contractFieldIds}
                contractVersionId={contractVersionId}
                legalEntityId={employeeData.legalEntity?.id}
              >
                <ContractCuesProvider
                  collection={{
                    hasCues: false,
                    incidenceRules: [],
                    listOfFields: observable.map(new Map()),
                  }}
                >
                  <Box gap="s24">
                    <Text.Normal weight="semibold" color="grey1000">
                      {i18n.t(
                        'processes.process_settings.tasks.form.super_tasks_module.data_collection.show.title'
                      )}
                    </Text.Normal>
                    <Text.Normal color="grey800">
                      {isContractCategories
                        ? i18n.t(
                            'processes.process_settings.tasks.form.super_tasks_module.data_collection.show.contract_description'
                          )
                        : i18n.t(
                            'processes.process_settings.tasks.form.super_tasks_module.data_collection.show.description'
                          )}
                    </Text.Normal>
                    <>
                      {<Box gap="s16">{categoriesElements()}</Box>}
                      <Footer />
                      {showAttachments && uploadDocumentText && !isIdentificationNumber && (
                        <>
                          <UploadDocuments
                            task={task}
                            title={uploadDocumentText.title}
                            description={uploadDocumentText.description}
                            employeeId={employeeId}
                            canEmployeeManageAnyEmployeeFolder={canEmployeeManageAnyEmployeeFolder}
                          />
                        </>
                      )}
                      {showSocialAndId && (
                        <IDAndSocialSecurityUploads
                          folderConfiguration={folderConfiguration}
                          task={task}
                          employeeId={employeeId}
                          canEmployeeManageAnyEmployeeFolder={canEmployeeManageAnyEmployeeFolder}
                        />
                      )}
                    </>
                  </Box>
                </ContractCuesProvider>
              </FixFlowProvider>
            </PoliciesProvider>
          </ContractVersionsPayrollPoliciesProvider>
        </LegalEntityContractDataProvider>
      </ContractFlowProvider>
    )
  }
)

const DataCollectionWrapper: FC<Props> = ({
  task,
  canSeePersonalInfo,
  canEditPersonalInfo,
  canEmployeeManageAnyEmployeeFolder,
  customResourceFieldsData,
  customValuesData,
  hasDevTimeTrackingWorkableDaysDefinition,
  hasTimeTrackingAnnualisationDailyValue,
  referenceContract,
  jobCatalogContextlessPolicies,
  hasPayrollBundle,
  contractContextlessPolicies,
  customFieldsContextlessPolicies,
}) => {
  return (
    <DataCollection
      task={task}
      canSeePersonalInfo={canSeePersonalInfo}
      canEditPersonalInfo={canEditPersonalInfo}
      canEmployeeManageAnyEmployeeFolder={canEmployeeManageAnyEmployeeFolder}
      customResourceFieldsData={customResourceFieldsData}
      customValuesData={customValuesData}
      hasDevTimeTrackingWorkableDaysDefinition={hasDevTimeTrackingWorkableDaysDefinition}
      hasTimeTrackingAnnualisationDailyValue={hasTimeTrackingAnnualisationDailyValue}
      referenceContract={referenceContract}
      jobCatalogContextlessPolicies={jobCatalogContextlessPolicies}
      hasPayrollBundle={hasPayrollBundle}
      contractContextlessPolicies={contractContextlessPolicies}
      customFieldsContextlessPolicies={customFieldsContextlessPolicies}
    />
  )
}

export default observer(DataCollectionWrapper)
